export const CALIBRATION_PASSAGE =
  "This calibration passage uses ordinary words and punctuation to measure the speaking rate of the selected voice. Listen until the reading ends, then the application calculates words per minute and stores that result only on this device. The passage contains no orders, names, locations, callsigns, grids, doctrine, personal information, or material from any trainee file. Its sole purpose is timing.";

export const WPM_PRESETS = Object.freeze({
  copy: 90,
  standard: 100,
} as const);

export type RateZone = "body" | "critical" | "heading" | "mission";

export interface CalibrationResult {
  readonly baselineWpm: number;
  readonly clampedMultiplier: number;
  readonly reachable: boolean;
  readonly requestedMultiplier: number;
  readonly targetWpm: number;
}

export function countWords(text: string): number {
  const trimmed = text.trim();
  return trimmed === "" ? 0 : trimmed.split(/\s+/).length;
}

export function targetWordsPerMinute(
  mode: "copy" | "free" | "pressure" | "standard",
  requested?: number,
): number {
  if (mode === "copy") return WPM_PRESETS.copy;
  if (mode === "standard") return WPM_PRESETS.standard;
  const minimum = mode === "pressure" ? 130 : 80;
  const maximum = 180;
  if (requested === undefined || !Number.isFinite(requested)) {
    throw new RangeError(`${mode} rate requires a finite value`);
  }
  if (requested < minimum || requested > maximum) {
    throw new RangeError(
      `${mode} rate must be ${String(minimum)} to ${String(maximum)} wpm`,
    );
  }
  return requested;
}

export function calculateCalibration(
  elapsedMs: number,
  targetWpm: number,
): CalibrationResult {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) {
    throw new RangeError("calibration elapsed time must be positive");
  }
  if (!Number.isFinite(targetWpm) || targetWpm <= 0) {
    throw new RangeError("target words per minute must be positive");
  }
  const baselineWpm = 60 / (elapsedMs / 1000 / 60);
  const requestedMultiplier = targetWpm / baselineWpm;
  const clampedMultiplier = Math.min(2, Math.max(0.5, requestedMultiplier));
  return {
    baselineWpm,
    targetWpm,
    requestedMultiplier,
    clampedMultiplier,
    reachable: requestedMultiplier >= 0.5 && requestedMultiplier <= 2,
  };
}

export function rateForZone(baseMultiplier: number, zone: RateZone): number {
  if (!Number.isFinite(baseMultiplier) || baseMultiplier <= 0) {
    throw new RangeError("base speech multiplier must be positive");
  }
  return zone === "critical" || zone === "mission"
    ? baseMultiplier * 0.85
    : baseMultiplier;
}
